/**
 * Manages mapping between simple identifiers (a, b, c) and blueprint class instances
 * Supports scoped identifiers for different entity types
 */
export class IdentifierManager {
	/**
	 * Map of identifier -> instance ID (UUID)
	 * Key format: "scope:identifier" (e.g., "islands:a", "panels:island1:a")
	 */
	private identifierToId: Map<string, string> = new Map();

	/**
	 * Reverse map: instance ID (UUID) -> identifier
	 */
	private idToIdentifier: Map<string, string> = new Map();

	/**
	 * Track next available identifier per scope
	 */
	private nextIdentifier: Map<string, number> = new Map();

	/**
	 * Register a mapping between an identifier and an instance ID
	 * @param scope The scope (e.g., 'islands', 'panels:island1')
	 * @param instanceId The UUID of the blueprint class instance
	 * @param identifier Optional identifier to use, otherwise generates next available
	 * @returns The identifier assigned
	 */
	public register (scope: string, instanceId: string, identifier?: string): string {
		const key = `${scope}:${identifier ?? this.generateNextIdentifier(scope)}`;

		// If identifier provided, check if it's already in use
		if (identifier) {
			const existingId = this.identifierToId.get(key);
			if (existingId && existingId !== instanceId) {
				throw new Error(`Identifier ${identifier} already in use in scope ${scope}`);
			}
		}

		// Remove old mapping if instanceId was already registered
		const oldKey = this.idToIdentifier.get(instanceId);
		if (oldKey) {
			this.identifierToId.delete(oldKey);
		}

		this.identifierToId.set(key, instanceId);
		this.idToIdentifier.set(instanceId, key);

		return key.split(':')[1]; // Return just the identifier part
	}

	/**
	 * Get the instance ID (UUID) for a given identifier in a scope
	 */
	public getId (scope: string, identifier: string): string | null {
		const key = `${scope}:${identifier}`;
		return this.identifierToId.get(key) ?? null;
	}

	/**
	 * Get the identifier for a given instance ID (UUID)
	 */
	public getIdentifier (instanceId: string): string | null {
		return this.idToIdentifier.get(instanceId) ?? null;
	}

	/**
	 * Get just the identifier part (without scope) for an instance ID
	 */
	public getIdentifierOnly (instanceId: string): string | null {
		const fullKey = this.idToIdentifier.get(instanceId);
		if (!fullKey) return null;
		return fullKey.split(':')[1];
	}

	/**
	 * Remove a mapping
	 */
	public unregister (scope: string, identifier: string): void {
		const key = `${scope}:${identifier}`;
		const instanceId = this.identifierToId.get(key);
		if (instanceId) {
			this.identifierToId.delete(key);
			this.idToIdentifier.delete(instanceId);
		}
	}

	/**
	 * Remove a mapping by instance ID
	 */
	public unregisterById (instanceId: string): void {
		const key = this.idToIdentifier.get(instanceId);
		if (key) {
			this.identifierToId.delete(key);
			this.idToIdentifier.delete(instanceId);
		}
	}

	/**
	 * Get all identifiers for a scope
	 */
	public getScopeIdentifiers (scope: string): Map<string, string> {
		const result = new Map<string, string>();
		for (const [key, instanceId] of this.identifierToId.entries()) {
			if (key.startsWith(`${scope}:`)) {
				const identifier = key.split(':')[1];
				result.set(identifier, instanceId);
			}
		}
		return result;
	}

	/**
	 * Clear all mappings for a scope
	 */
	public clearScope (scope: string): void {
		const keysToDelete: Array<string> = [];
		for (const key of this.identifierToId.keys()) {
			if (key.startsWith(`${scope}:`)) {
				keysToDelete.push(key);
			}
		}
		for (const key of keysToDelete) {
			const instanceId = this.identifierToId.get(key);
			if (instanceId) {
				this.identifierToId.delete(key);
				this.idToIdentifier.delete(instanceId);
			}
		}
	}

	/**
	 * Clear all mappings
	 */
	public clear (): void {
		this.identifierToId.clear();
		this.idToIdentifier.clear();
		this.nextIdentifier.clear();
	}

	/**
	 * Generate the next available identifier for a scope
	 */
	private generateNextIdentifier (scope: string): string {
		const current = this.nextIdentifier.get(scope) ?? 0;
		this.nextIdentifier.set(scope, current + 1);
		return this.numberToIdentifier(current);
	}

	/**
	 * Convert a number to an identifier (0 -> a, 1 -> b, ..., 25 -> z, 26 -> aa, ...)
	 */
	private numberToIdentifier (num: number): string {
		if (num < 26) {
			return String.fromCharCode(97 + num); // 'a' = 97
		}
		// For numbers >= 26, use double letters: aa, ab, ac, ...
		const first = Math.floor(num / 26) - 1;
		const second = num % 26;
		return String.fromCharCode(97 + first) + String.fromCharCode(97 + second);
	}
}
